import { collection, addDoc, getDocs } from "firebase/firestore";
import { db } from "../firebaseConfig.js";

/**
 * Submit booking feedback to Firebase
 * @param {boolean} isPositive - Whether the feedback is positive (true) or negative (false)
 * @param {Object} bookingDetails - Optional booking details for context
 * @returns {Promise} - Promise that resolves when feedback is submitted
 */
export const submitBookingFeedback = async (isPositive, bookingDetails = {}) => {
  try {
    const feedbackData = {
      isPositive: isPositive,
      feedbackType: "booking_process",
      timestamp: new Date(),
      rating: isPositive ? "positive" : "negative",
      // Optional: Include some booking context (without sensitive data)
      context: {
        service: bookingDetails.service || null,
        subService: bookingDetails.subService || null,
        hasPrice: !!bookingDetails.price,
        hasDuration: !!bookingDetails.duration,
      },
      // Add session info for analytics
      sessionInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        timestamp: Date.now(),
      }
    };

    // Add to UTA-feedback collection in Firebase
    const feedbackCollection = collection(db, "UTA-feedback");
    const docRef = await addDoc(feedbackCollection, feedbackData);
    
    console.log("Booking feedback submitted successfully:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error submitting booking feedback:", error);
    throw error;
  }
};

/**
 * Get feedback statistics (for admin use)
 * @returns {Promise<Object>} - Feedback statistics
 */
export const getFeedbackStats = async () => {
  try {
    const feedbackCollection = collection(db, "UTA-feedback");
    const snapshot = await getDocs(feedbackCollection);
    
    let positive = 0;
    let negative = 0;
    let total = 0;
    const serviceBreakdown = {};
    const recentFeedback = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      total++;
      
      // Count positive/negative
      if (data.isPositive) {
        positive++;
      } else {
        negative++;
      }
      
      // Service breakdown
      const service = data.context?.service || 'Unknown';
      if (!serviceBreakdown[service]) {
        serviceBreakdown[service] = { positive: 0, negative: 0, total: 0 };
      }
      serviceBreakdown[service].total++;
      if (data.isPositive) {
        serviceBreakdown[service].positive++;
      } else {
        serviceBreakdown[service].negative++;
      }
      
      // Recent feedback (last 10)
      recentFeedback.push({
        id: doc.id,
        isPositive: data.isPositive,
        service: service,
        timestamp: data.timestamp?.toDate?.() || new Date(data.timestamp),
        platform: data.sessionInfo?.platform || 'Unknown'
      });
    });
    
    // Sort recent feedback by timestamp (newest first)
    recentFeedback.sort((a, b) => b.timestamp - a.timestamp);
    
    return {
      total,
      positive,
      negative,
      positiveRate: total > 0 ? ((positive / total) * 100).toFixed(1) : 0,
      negativeRate: total > 0 ? ((negative / total) * 100).toFixed(1) : 0,
      serviceBreakdown,
      recentFeedback: recentFeedback.slice(0, 10), // Last 10 feedback
      lastUpdated: new Date()
    };
  } catch (error) {
    console.error("Error getting feedback stats:", error);
    throw error;
  }
};

/**
 * Get detailed feedback insights
 * @returns {Promise<Object>} - Detailed insights and recommendations
 */
export const getFeedbackInsights = async () => {
  try {
    const stats = await getFeedbackStats();
    
    const insights = {
      ...stats,
      recommendations: [],
      trends: {},
      alerts: []
    };
    
    // Generate recommendations based on data
    if (stats.total === 0) {
      insights.recommendations.push({
        type: 'info',
        title: 'No Data Available',
        message: 'Start collecting feedback to see insights and recommendations.'
      });
    } else if (stats.total < 10) {
      insights.recommendations.push({
        type: 'info',
        title: 'Collect More Data',
        message: `You have ${stats.total} feedback responses. Collect at least 10 for meaningful insights.`
      });
    } else {
      // Satisfaction analysis
      const satisfactionRate = parseFloat(stats.positiveRate);
      
      if (satisfactionRate >= 90) {
        insights.recommendations.push({
          type: 'success',
          title: 'Excellent Performance!',
          message: 'Your booking process is highly satisfactory. Consider sharing success metrics.'
        });
      } else if (satisfactionRate >= 75) {
        insights.recommendations.push({
          type: 'good',
          title: 'Good Performance',
          message: 'Good satisfaction rate. Look for minor improvements to reach excellence.'
        });
      } else if (satisfactionRate >= 50) {
        insights.recommendations.push({
          type: 'warning',
          title: 'Needs Improvement',
          message: 'Consider reviewing the booking process for usability issues.'
        });
      } else {
        insights.recommendations.push({
          type: 'critical',
          title: 'Critical Issues',
          message: 'Low satisfaction rate requires immediate attention and process review.'
        });
        insights.alerts.push('Low satisfaction rate detected!');
      }
      
      // Service-specific insights
      Object.entries(stats.serviceBreakdown).forEach(([service, data]) => {
        const serviceRate = data.total > 0 ? ((data.positive / data.total) * 100).toFixed(1) : 0;
        if (data.total >= 3 && serviceRate < 60) {
          insights.alerts.push(`${service} service has low satisfaction: ${serviceRate}%`);
        }
      });
    }
    
    return insights;
  } catch (error) {
    console.error("Error getting feedback insights:", error);
    throw error;
  }
};